import { onScopeDispose, type ShallowRef, shallowRef } from "vue";
import type { ResourceClient } from "./resource-client.js";
import type { AgentStore } from "./store.js";
import type { AgentStoreSnapshot, ResourceClientSnapshot } from "./types.js";

/**
 * Subscribe to an {@link AgentStore} and return a reactive snapshot ref.
 *
 * Uses `shallowRef` for fine-grained reactivity — Vue templates and computed
 * properties that read `snapshot.value.messages` etc. will update automatically.
 * The subscription is cleaned up automatically when the owning scope is disposed
 * (e.g. when the component unmounts).
 *
 * @param store - The store to subscribe to.
 * @returns A `ShallowRef` whose `.value` is the current immutable state snapshot.
 *
 * @example
 * ```vue
 * <script setup>
 * import { useAgentStore } from '@skaile/workspace-bridge-vue'
 *
 * const props = defineProps<{ store: AgentStore }>()
 * const snapshot = useAgentStore(props.store)
 * // snapshot.value.messages, snapshot.value.streamingText, etc.
 * </script>
 * ```
 * @docLink packages/store/concepts#vue-composables
 */
export function useAgentStore(store: AgentStore): ShallowRef<AgentStoreSnapshot> {
  const snapshot = shallowRef(store.getSnapshot());

  const unsub = store.subscribe(() => {
    snapshot.value = store.getSnapshot();
  });

  onScopeDispose(unsub);

  return snapshot;
}

/**
 * Subscribe to a {@link ResourceClient} and return a reactive snapshot ref.
 *
 * Re-renders only when the agent emits a `resources_available` event (typically
 * once on connect). The subscription is cleaned up automatically when the owning
 * scope is disposed.
 *
 * @param client - The resource client to subscribe to.
 * @returns A `ShallowRef` whose `.value` is the current resource snapshot.
 *
 * @example
 * ```vue
 * <script setup>
 * import { useResourceClient } from '@skaile/workspace-bridge-vue'
 *
 * const props = defineProps<{ client: ResourceClient }>()
 * const resources = useResourceClient(props.client)
 * // resources.value.mounts, resources.value.connectors
 * </script>
 * ```
 * @docLink packages/store/concepts#vue-composables
 */
export function useResourceClient(client: ResourceClient): ShallowRef<ResourceClientSnapshot> {
  const snapshot = shallowRef(client.getSnapshot());

  const unsub = client.subscribe(() => {
    snapshot.value = client.getSnapshot();
  });

  onScopeDispose(unsub);

  return snapshot;
}
